import {
  alpha,
  Autocomplete,
  AutocompleteProps,
  Chip,
  InputAdornment,
  TextField,
  TextFieldProps,
  useTheme,
} from "@mui/material";
import { Entity } from "portal/components/filters/filter.types";
import { getAutocompleteText } from "portal/utils/forms";
import { useInfiniteScrollOnScroll } from "portal/utils/hooks/useInfiniteScroll";
import { useTranslation } from "react-i18next";
import React, { FunctionComponent, useCallback } from "react";
import WarningIcon from "@mui/icons-material/WarningAmber";

type ServerPagingProps = {
  onLoadMore: () => void;
  onSearchInputChange: (text: string) => void;
};

type FilterProps = Pick<
  AutocompleteProps<Entity, true, true, false>,
  "loading" | "options" | "value"
> & {
  onChange: (value: Entity[]) => void;
  loadingError?: boolean;
  label: TextFieldProps["label"];
} & (ServerPagingProps | { [K in keyof ServerPagingProps]?: never });

export const AutocompleteFilter: FunctionComponent<FilterProps> = ({
  loading,
  options,
  value,
  label,
  onChange,
  loadingError = false,
  onLoadMore,
  onSearchInputChange,
}) => {
  const { t } = useTranslation();
  const muiTheme = useTheme();
  const loadMore = useCallback(() => {
    onLoadMore?.();
  }, [onLoadMore]);
  const handleListboxScroll =
    useInfiniteScrollOnScroll<HTMLUListElement>(loadMore);
  return (
    <Autocomplete<Entity, true, true, false>
      {...getAutocompleteText(t)}
      size="small"
      multiple
      loading={loading}
      options={options}
      value={value}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      limitTags={3}
      {...(onSearchInputChange
        ? {
            filterOptions: (options: Entity[]) => options,
            onInputChange: (
              _: React.SyntheticEvent,
              nextInputValue: string,
              reason: string
            ) => {
              if (reason === "input" || reason === "clear") {
                onSearchInputChange(nextInputValue);
              }
            },
            slotProps: { listbox: { onScroll: handleListboxScroll } },
          }
        : {})}
      renderValue={(tagValue, getItemProps) => {
        return tagValue.map((option, index) => (
          <Chip
            {...getItemProps({ index })}
            key={option.id}
            label={option.name}
            size="small"
            style={{
              backgroundColor:
                muiTheme.palette.mode === "dark"
                  ? alpha(muiTheme.palette.common.white, 0.2)
                  : alpha(muiTheme.palette.common.white, 0.8),
              color: muiTheme.palette.text.primary,
            }}
            classes={{
              deleteIcon: undefined,
            }}
            sx={{
              "& .MuiChip-deleteIcon": {
                color: muiTheme.palette.text.primary,
              },
            }}
          />
        ));
      }}
      onChange={(_: React.SyntheticEvent<Element, Event>, value) => {
        onChange(value);
      }}
      renderInput={(parameters: TextFieldProps) => {
        const inputSlotProps = parameters.slotProps?.input;
        return (
          <TextField
            {...parameters}
            size="small"
            className="min-w-32 md:min-w-48"
            label={label}
            slotProps={{
              ...parameters.slotProps,

              input: {
                ...inputSlotProps,
                size: "small",
                endAdornment: (
                  <>
                    {loadingError && (
                      <InputAdornment
                        position="end"
                        className="text-sm"
                        style={{ color: muiTheme.palette.warning.main }}
                      >
                        <WarningIcon />
                      </InputAdornment>
                    )}
                    {typeof inputSlotProps === "function"
                      ? undefined
                      : inputSlotProps?.endAdornment}
                  </>
                ),
              },
            }}
          />
        );
      }}
    />
  );
};
